import React, { useEffect, useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import {
  BenchmarkStatus,
  BenchmarkStatusDefaultColors,
  BenchmarkStatusDisplayedNames,
  ColorCriteria,
  ColorCriteriaBenchmarkNames,
  ColorCriteriaGrade,
  EvidencePeriodData,
  GradeLevels,
  Measurement,
  MeasurementType,
  ReactSelectOption,
} from "../../../store/onboarding/types";
import SketchColorPicker from "./SketchColorPicker";
import Button from "react-bootstrap/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColorResult, GithubPicker } from "react-color";
import { Form, Spinner } from "react-bootstrap";
import { faExclamationTriangle, faLock, faTimes } from "@fortawesome/free-solid-svg-icons";
import { IS_READY_COACH } from "../../../constants";
import {
  Grade,
  TimePeriod,
  TimePeriodShortOptions,
} from "../../../store/groups/types";
import Select from "react-select";
import { connect, useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { GoalTypes } from "../../../store/onboarding/cases/types";
import { FormattedMessage } from "react-intl";
import { showConfirmDialog } from "../../../store/confirm-dialog/actions";
import LoadingIndicator from "../onboarding/LoadingIndicator";
import { toastr } from "react-redux-toastr";
import { UserInfo } from "../../../store/auth/types";

type PropsFromState = {
  enterDataManuallyType?: GoalTypes;
  searchFilter?: any;
};

type Props = PropsFromState & {
  showModal: boolean;
  withGrades?: boolean;
  withReadingAlgorithm?: boolean;
  useGradeSpecificColor?: boolean;
  selectedGrade?: Grade;
  selectedTimePeriod?: TimePeriod;
  benchmark_status?: string;
  measurement?: Measurement;
  onHide: () => void;
  onApplyChanges: (measurement: Measurement, searchFilter?: any) => any;
  isLoading?: boolean;
  evidenceColumnGroupId?: number | undefined;
  filterDisabled?: boolean;
  setFilterDisabled?: React.Dispatch<React.SetStateAction<any | undefined>>;
  searchFilter?: any;
  onHandleSearchFilter?: (searchFilter: any) => void;
  originalColorCriteria?: ColorCriteria[];
  fromViewBenchmarks?: boolean;
  isSystemAssessment?: boolean;
  editMeasureColorIndex?: number;
  modes?: 'create' | 'edit' | 'view';
  benchmarkLabel?: string;
  isRemoveDefaultBenchmarks?: boolean;
  onRemoveDefaultBenchmarks?: (isRemoveDefaultBenchmarks: boolean) => any;
};

const AT_ORDER_KEY = 3; // Constant to ensure "Average" benchmark appears in the correct order when added back in

const normalizeBenchmarkStatusValue = (benchmarkStatus?: string) => {
  const normalizedValue = benchmarkStatus?.trim().toLowerCase();

  switch (normalizedValue) {
    case "at":
    case "average":
      return "at";
    case "at_or_above":
      return "at_or_above";
    case "well_below":
    case "below":
      return "below";
    default:
      return benchmarkStatus || "below";
  }
};

const EditMeasureColorCriteriaModal: React.FC<Props> = (props) => {
  const dispatch = useDispatch();

  let [measurement, setMeasurement] = useState<Measurement | undefined>();
  const [localSearchFilterState, setLocalSearchFilterState] = useState<any>();
  const [colorPicker, setColorPicker] = useState<{
    target?: number;
    display: boolean;
  }>({
    display: false,
    target: undefined,
  });

  const currentUser: UserInfo = useSelector(
    (s: ApplicationState) => s.auth.userInfo!
  );
  const isAllowedToEdit = useMemo(() => {
    if (measurement) {
      const userEditPermission = (props.modes !== 'view') && (props.modes !== 'edit') ? currentUser.allowed_edit_benchmarks : true;
      const isCustomColorCriteriaAvailable =
        measurement.custom_color_criteria &&
          measurement.custom_color_criteria.length
          ? true
          : false;
      const isGradeSpecificColorCriteriaAvailable = measurement.grade_specific_color_criteria
        ? true
        : false;
      const isCreator = measurement?.custom_color_criteria && Array.isArray(measurement?.custom_color_criteria)
        ? !!measurement?.custom_color_criteria?.find((et) =>
          et && et.user_id ? et.user_id == currentUser.id : false
        )
        : false;
      return (
        userEditPermission ||
        isCreator ||
        (!isCustomColorCriteriaAvailable &&
          !isGradeSpecificColorCriteriaAvailable)
      );
    }
    return false;
  }, [measurement]);

  const evidencePeriodData = useSelector<ApplicationState, EvidencePeriodData | undefined>(
    (s: any) => s.onboarding.evidencePeriodData);

  useEffect(() => {

    const withReadAlgoCheck = evidencePeriodData?.evidence_column_groups.find(
      (evidenceData) => evidenceData.assessment !== null && evidenceData.id == props.evidenceColumnGroupId);

    if ((withReadAlgoCheck != null) &&
      (withReadAlgoCheck?.assessment.is_system_assessment == false)
      && (withReadAlgoCheck?.assessment.is_behavior == false)) {
      if (withReadAlgoCheck?.assessment.use_grade_specific_color_criteria == false) {
        props.setFilterDisabled!(true);
      }
    }

  }, [evidencePeriodData])

  useEffect(() => {
    setLocalSearchFilterState(props.searchFilter);
  }, [props.searchFilter]);

  useEffect(() => {
    if (localSearchFilterState && localSearchFilterState.filterGrade && localSearchFilterState.filterGrade.length > 0) {
      filterGrid();
    }
  },[props.onHandleSearchFilter, localSearchFilterState]);

  useEffect(() => {
    colorCriteriaValidation(measurement?.color_criteria!, false)
  }, [measurement?.color_criteria])
  // const handleSearchFilter = () => {
  //   props.onHandleSearchFilter?.(localSearchFilterState);
  // }

  const handleShowModal = () => {
     if (!props.measurement) {
      return;
    }
    const hasColorCriteria =
      props.measurement.color_criteria &&
      props.measurement.color_criteria.length > 0;

    setMeasurement(
      hasColorCriteria
        ? !props.useGradeSpecificColor &&
          props.withGrades &&
          props.measurement.color_criteria?.some(
            (cc) =>
              cc.grade === props.selectedGrade &&
              cc.time_period === props.selectedTimePeriod
          )
          ? {
            ...props.measurement,
            color_criteria: [
              ...(props.measurement.color_criteria ?? []),
              //getDefaultColorCriteria(props.measurement!),
            ],
          }
          : props.measurement
        : {
          ...props.measurement,
          color_criteria: [
            getDefaultColorCriteria(
              props.measurement, 
              BenchmarkStatus.Below, 
              BenchmarkStatusDefaultColors[BenchmarkStatus.Below]
            ), 
            getDefaultColorCriteria(
              props.measurement, 
              BenchmarkStatus.AtOrAbove, 
              BenchmarkStatusDefaultColors[BenchmarkStatus.AtOrAbove]
            )],
        }
    );
    const filterGrade = (localSearchFilterState != undefined) ? localSearchFilterState.filterGrade : null;
    if (filterGrade !== null && filterGrade.length > 0) {
      handleFilterGradeChange(filterGrade);
      setTimeout(() => { filterGrid(); }, 100);
    }

    props.onRemoveDefaultBenchmarks?.(false);
  };


  const getValueWithMinOrder = (m: Measurement) => {
    if (
      measurement &&
      measurement.color_criteria &&
      measurement.color_criteria.length > 0
    ) {
      return m
        .possible_values!.filter(
          (pValue) =>
            !measurement?.color_criteria!.some(
              (cc) => cc.selected_value_order === pValue.order
            )
        )
        .reduce((prev, curr) => (prev.order > curr.order ? prev : curr));
    } else {
      return m.possible_values!.reduce((prev, curr) =>
        prev.order < curr.order ? prev : curr
      );
    }
  };

  const getDefaultColorCriteria = (
    measurement: Measurement, 
    benchmarkStatus?: BenchmarkStatus, 
    defaultColor?: string,
    orderKey?: number
  ): ColorCriteria => {
    if (
      measurement.type === MeasurementType.CATEGORY &&
      measurement.possible_values
    ) {
      const valueWithMinOrder = getValueWithMinOrder(measurement);
      return {
        grade: props.selectedGrade,
        time_period: props.selectedTimePeriod,
        benchmark_status: benchmarkStatus || "below",
        display_name: valueWithMinOrder.display_name,
        range_min: undefined,
        range_max: undefined,
        order: valueWithMinOrder.order,
        selected_value_order: valueWithMinOrder.order,
        color: "#ffffff",
        user_id: currentUser.id,
      };
    } else {
      return {
        grade: props.selectedGrade,
        time_period: props.selectedTimePeriod,
        benchmark_status: benchmarkStatus || "below",
        display_name: benchmarkStatus ? BenchmarkStatusDisplayedNames[benchmarkStatus] : "",
        range_min: undefined,
        range_max: undefined,
        order:
          measurement.color_criteria && measurement.color_criteria.length > 0
            ? measurement.color_criteria.reduce((prev, curr) =>
              prev.order! > curr.order! ? prev : curr
            ).order! + 1
            : 1,
        color: defaultColor ?? "#ffffff",
        user_id: currentUser.id,
        isVisible: props.useGradeSpecificColor ? orderKey !== AT_ORDER_KEY : true,
      };
    }
  };

  const handleColorCriteriaChange = (selectedColorCriteria: ColorCriteria) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;

    setMeasurement((measurement) =>
      measurement
        ? {
          ...measurement,
          color_criteria: measurement.color_criteria!.map(
            (colorCriteria: ColorCriteria) => {
              if (colorCriteria === selectedColorCriteria) {
                if (name === "selected_value_order") {
                  const order = value ? +value : undefined;
                  const dName = order
                    ? measurement.possible_values!.find(
                      (pValue) => pValue.order === order
                    )!.display_name
                    : "";
                  colorCriteria = {
                    ...colorCriteria,
                    order: order,
                    selected_value_order: order,
                    display_name: dName,
                  };
                } else if (name === "grade" || name === "time_period" || name === "display_name") {
                  colorCriteria = {
                    ...colorCriteria,
                    [name]: value ? value : undefined,
                  };
                }
                return {
                  ...colorCriteria,
                  [name]:
                    name === "range_max" || name === "range_min"
                      ? value
                        ? +value
                        : undefined
                      : value,
                }
              } else {
                return colorCriteria;
              }
            }
          ),
        }
        : undefined
    );
  };


  const handleColorCriteriaRemove = (
    selectedColorCriteria: ColorCriteria,
    index: number
  ) => () => {
    setMeasurement((measurement) =>
      measurement
        ? {
          ...measurement,
          color_criteria:
            measurement.color_criteria?.filter((_, i) => index !== i) ?? [],
          // measurement.color_criteria?.filter((colorCriteria) =>
          //   props.useGradeSpecificColor
          //     ? !(
          //       colorCriteria.grade === selectedColorCriteria.grade &&
          //       colorCriteria.time_period ===
          //       selectedColorCriteria.time_period
          //     )
          //     : colorCriteria !== selectedColorCriteria
          // ) ?? [],
        }
        : undefined
    );
  };

  const handleColorPickerClick = (index: number) => () => {
    setColorPicker((colorPicker) => ({
      target: index,
      display: !colorPicker.display,
    }));
  };

  const handleColorPickerChange = (selectedColorCriteria: ColorCriteria) => (
    color: ColorResult
  ) => {
    if(props.modes === 'view') return;
    setMeasurement((measurement) =>
      measurement
        ? {
          ...measurement,
          color_criteria: measurement.color_criteria!.map(
            (colorCriteria: ColorCriteria) => {
              if (colorCriteria === selectedColorCriteria) {
                return {
                  ...colorCriteria,
                  color: color.hex,
                };
              }
              return colorCriteria;
            }
          ),
        }
        : undefined
    );
  };

  const sortingBenchmarkStatusArray = [
    BenchmarkStatus.WellBelow,
    BenchmarkStatus.Below,
    BenchmarkStatus.At,
    BenchmarkStatus.AtOrAbove
  ];

  const handleColorCriteriaAdd = (event: React.FormEvent) => {
    event.preventDefault();
    setMeasurement((measurement) => {
      if (!measurement) return undefined;
      const existingStatuses = measurement.color_criteria?.map(cc => cc.benchmark_status) || [];
      let newStatus: BenchmarkStatus | undefined;

      if (!existingStatuses.includes(BenchmarkStatus.WellBelow)) {
        newStatus = BenchmarkStatus.WellBelow;
      }
      if (!existingStatuses.includes(BenchmarkStatus.Below)) {
        newStatus = BenchmarkStatus.Below;
      }
      if (!existingStatuses.includes(BenchmarkStatus.At)) {
        newStatus = BenchmarkStatus.At;
      }
      if (!existingStatuses.includes(BenchmarkStatus.AtOrAbove)) {
        newStatus = BenchmarkStatus.AtOrAbove;
      }

      const measurementData = {
        ...measurement,
        color_criteria: [
          ...(measurement.color_criteria ?? []),
          getDefaultColorCriteria(
            measurement, 
            newStatus, 
            newStatus ? BenchmarkStatusDefaultColors[newStatus]: "#ffffff"),
        ].sort((a, b) =>
          sortingBenchmarkStatusArray.indexOf(a.benchmark_status as BenchmarkStatus)
          - sortingBenchmarkStatusArray.indexOf(b.benchmark_status as BenchmarkStatus)
        ),
      };
      return measurementData;
    });
  };

  const handleColorPickerClose = () => {
    setColorPicker({ target: undefined, display: false });
  };


  const handleFilterGradeChange = (selectedOption: any) => {
    if (selectedOption != null) {
      if (localSearchFilterState) {
        // Sort selected grades before saving to state
        const gradeOrder = ["K", ...Array.from({ length: 12 }, (_, i) => `G${i + 1}`)];
        const sortedSelectedOption = [...selectedOption].sort(
          (a, b) => gradeOrder.indexOf(a.value) - gradeOrder.indexOf(b.value)
        );
        localSearchFilterState.filterGrade = sortedSelectedOption;

        const TimePeriodShortOptions = [
          { value: TimePeriod.Beginning, label: "Beginning" },
          { value: TimePeriod.Middle, label: "Middle" },
          { value: TimePeriod.End, label: "End" },
        ];

        // Dynamically handle grades K to G12
        gradeOrder.forEach((grade) => {
          const selectKey = `selectFor${grade}`;
          if (
            sortedSelectedOption.some((g: any) => g.value === grade) &&
            !(localSearchFilterState[selectKey] && localSearchFilterState[selectKey].length)
          ) {
            localSearchFilterState[selectKey] = [
              ...(localSearchFilterState[selectKey] || []),
              ...TimePeriodShortOptions.map((tp: any) => ({
                ...tp,
                grade,
              })),
            ];
          }
        });

        // Remove time periods for grades that are no longer selected
        gradeOrder.forEach((grade) => {
          const selectKey = `selectFor${grade}`;
          if (
            localSearchFilterState &&
            !sortedSelectedOption.some((g: any) => g.value === grade)
          ) {
            localSearchFilterState[selectKey] = [];
          }
        });

        props.onHandleSearchFilter?.(localSearchFilterState);
      }
    } else {
      // Reset all grades and time periods
      const gradeOrder = ["K", ...Array.from({ length: 12 }, (_, i) => `G${i + 1}`)];
      const updatedSearchFilter = {
        ...localSearchFilterState,
        filterIndex: 0,
        filterGrade: [],
        filterAdditionalBenchmark: true,
        ...gradeOrder.reduce((acc, grade) => {
          acc[`selectFor${grade}`] = [];
          return acc;
        }, {} as any),
      };
      props.onHandleSearchFilter?.(updatedSearchFilter);
    }
  };

  const handleFilterTimePeriodChange = (selectedOption: any, grade: any) => {
    const selectKey = `selectFor${grade}`;
    if (selectedOption != null) {
      localSearchFilterState[selectKey] = selectedOption.map((tp: any) => ({
        value: tp.value,
        label: tp.label,
        grade,
      }));
    } else {
      localSearchFilterState[selectKey] = [];
    }
    if(localSearchFilterState[selectKey].length == 0) {
      let grade = selectKey.replace("selectFor", "");
      localSearchFilterState.filterGrade = localSearchFilterState.filterGrade.filter((g: any) => g.value !== grade);
    }
    props.onHandleSearchFilter?.(localSearchFilterState);
  };

  const str2bool = (value: any) => {
    if (value && typeof value === "string") {
      if (value.toLowerCase() === "true") return true;
      if (value.toLowerCase() === "false") return false;
    }
    return value;
  }

  const handleAdditionalBanchmarkChange = (event: React.FormEvent) => {
    const { value } = event.target as HTMLInputElement;

    props.onHandleSearchFilter?.({
      ...localSearchFilterState,
      filterAdditionalBenchmark: str2bool(value)
    });
    //setMeasurement(measurement);
  };

  const colorCriteriaValidation = (colorCriteriaArray: ColorCriteria[], isSubmit: boolean) => {
    // Only validate visible color criteria (isVisible !== false)
    let isValid = true;

    // If grade-specific color is enabled, ensure at least one grade and time period is selected
    if (props.useGradeSpecificColor) {
      const hasGrade =
        localSearchFilterState?.filterGrade &&
        localSearchFilterState.filterGrade.length > 0;
      if (!hasGrade && isSubmit) {
        let gradeSelectEl: HTMLElement | null = document.getElementById("filterGradeSelect");
        let gradeSelectErrorEl: HTMLElement | null = document.getElementById("filterGradeSelectError");
        if(gradeSelectEl) {
          gradeSelectEl?.style.setProperty("border", "1px solid red");
        }
        if(gradeSelectErrorEl) {
          gradeSelectErrorEl.style.display = "block";
        }
        isValid = false;
      }
    }
    
    const colorCriteriaList = colorCriteriaArray
      ?.filter((colorCriteria) =>
        props.useGradeSpecificColor
          ? colorCriteria.isVisible !== false
          : true
      ) || [];
  
    for (let k = 0; k < colorCriteriaList.length; k++) {
      const element = colorCriteriaList[k];
      if (
        element.range_min != null &&
        element.range_max != null &&
        element.range_min >= element.range_max
      ) {
        let content = document.getElementById(`range_compare_${k}`);
        if (content) {
          content.textContent = "Min > Max.";
          isValid = false;
        }
      } else {
        let content = document.getElementById(`range_compare_${k}`);
        if (content) {
          content.textContent = "";
        }
      }
    }
  
    colorCriteriaList.forEach((_, idx) => {
      const content = document.getElementById(`range_overlap_${idx}`);
      if (content) content.textContent = "";
    });
  
    for (let i = 0; i < colorCriteriaList.length; i++) {
      const a = colorCriteriaList[i];
      if (a.range_min == null || a.range_max == null) continue;
  
      for (let j = i + 1; j < colorCriteriaList.length; j++) {
        const b = colorCriteriaList[j];
        if (b.range_min == null || b.range_max == null) continue;
  
        // Group by Grade + Time Period
        if (a.grade !== b.grade || a.time_period !== b.time_period) {
          continue;
        }
  
        // Overlap check
        const isOverlap =
          a.range_min <= b.range_max &&
          a.range_max >= b.range_min;
  
        if (isOverlap) {
          // Mark ONLY the latest row (j)
          const content = document.getElementById(`range_overlap_${j}`);
          if (content) {
            content.textContent = "Range overlapped.";
            isValid = false;
            const colorCriteriaRow = document.getElementById(
              `colorCriteriaRow_${j}`
            );
            if (colorCriteriaRow) {
              colorCriteriaRow.classList.add("color-criteria-align-top");
            }
          }
        }
      }
    }
  
    return isValid;
  };
  const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (measurement) {
      if (colorCriteriaValidation(measurement.color_criteria!, true)) {
        let modifiedMeasurement = { ...measurement };
        if (props.useGradeSpecificColor && props.enterDataManuallyType == 'academic') {
          if (localSearchFilterState.filterAdditionalBenchmark == false) {
            modifiedMeasurement.color_criteria = modifiedMeasurement.color_criteria?.map(
              (colorCriteria) => {
                if (colorCriteria.order !== AT_ORDER_KEY) {
                  return {
                    ...colorCriteria,
                    isVisible: true
                  };
                } else {
                  return {
                    ...colorCriteria,
                    isVisible: false
                  };
                }
              });
  
            (modifiedMeasurement.color_criteria!.length > 0) && modifiedMeasurement.color_criteria?.map((data, index) => {
              data.display_name = modifiedMeasurement.color_criteria?.[index].display_name!;
              data.range_max = modifiedMeasurement.color_criteria?.[index].range_max!;
              data.range_min = modifiedMeasurement.color_criteria?.[index].range_min!;
              data.color = modifiedMeasurement.color_criteria?.[index].color!;
              return data;
            });
          }
        }
        props.onApplyChanges(modifiedMeasurement, localSearchFilterState);
      }
    }
  };

  const refCallback = (colorCriteria: ColorCriteria) => (element: any) => {
    if (element) {
      if (colorCriteria.range_min == undefined || colorCriteria.range_max == undefined) {
        (element as HTMLInputElement).setCustomValidity("");
        return;
      }
      // if (colorCriteria.range_min >= colorCriteria.range_max) {
      //   (element as HTMLInputElement).setCustomValidity(
      //     "The minimum range must be less than the maximum range"
      //   );
      //   return;
      // }
      // if (!measurement) return;
      // const overlappingColorCriteria =
      //   colorCriteria.range_min &&
      //   measurement.color_criteria!.find(
      //     (cc) =>
      //       cc.grade == colorCriteria.grade &&
      //       cc.time_period == colorCriteria.time_period &&
      //       cc.order !== colorCriteria.order &&
      //       cc.range_min &&
      //       cc.range_max &&
      //       colorCriteria.range_min! >= cc.range_min &&
      //       colorCriteria.range_min! <= cc.range_max
      //   );
      // if (overlappingColorCriteria) {
      //   (element as HTMLInputElement).setCustomValidity(
      //     `The benchmark values overlap with the following color criteria: ${overlappingColorCriteria.display_name} Score Range: ${overlappingColorCriteria.range_min}-${overlappingColorCriteria.range_max}`
      //   );
      // } else {
      //   (element as HTMLInputElement).setCustomValidity("");
      // }
    }
  };

  const isCategoryType = useMemo(() => {
    return measurement?.type === MeasurementType.CATEGORY;
  }, [measurement?.type]);

  const isDisabledButton = useMemo(() => {
    return (
      measurement?.color_criteria
        ?.filter(c => props.useGradeSpecificColor ? 
          c.isVisible && (
            localSearchFilterState.filterAdditionalBenchmark 
            ? c.order == AT_ORDER_KEY 
            : c.order !== AT_ORDER_KEY) 
        : true)
        .some(
          c =>
            (c.range_min == undefined) ||
            (c.range_max == undefined)
        )
    );
  }, [measurement?.color_criteria]);


  const options = GradeLevels.filter((grade) => currentUser.allowed_grades.length ? currentUser.allowed_grades.includes(grade.value) : true).map((data) => {
    return { 'label': data.value, 'value': data.value }
  });

  const handleCloseModal = () => {
    props.onHide();
  }

  const filterGrid = () => {
    if (!measurement || !localSearchFilterState) return;

    const { filterGrade, filterAdditionalBenchmark } = localSearchFilterState;

    if(filterGrade && filterGrade.length > 0) {
      let gradeSelectEl: HTMLElement | null = document.getElementById("filterGradeSelect");
      let gradeSelectErrorEl: HTMLElement | null = document.getElementById("filterGradeSelectError");
      if(gradeSelectEl) {
        gradeSelectEl?.style.removeProperty("border");
      }
      if(gradeSelectErrorEl) {
        gradeSelectErrorEl.style.display = "none";
      }
    }

    // Dynamically collect all time period filters for grades K to G12
    const gradeOrder = ["K", ...Array.from({ length: 12 }, (_, i) => `G${i + 1}`)];
    const timePeriodFilters = gradeOrder.flatMap(
      grade => localSearchFilterState[`selectFor${grade}`] || []
    );

    // Restore "At" if switching from 3 → 4 and it's missing
    let colorCriteriaArr = measurement.color_criteria ? [...measurement.color_criteria] : [];
    if (
      (filterAdditionalBenchmark === 4 || filterAdditionalBenchmark === true) &&
      !colorCriteriaArr.some(cc => cc.order === AT_ORDER_KEY)
    ) {
      colorCriteriaArr.push(
        getDefaultColorCriteria(
          measurement, 
          BenchmarkStatus.At, 
          BenchmarkStatusDefaultColors[BenchmarkStatus.At],
          AT_ORDER_KEY //orderkey to ensure "At" appears in the correct order when added back in
        )
      );
    }

    const filteredColorCriteria =
      colorCriteriaArr.map((cc: ColorCriteria) => {
        // ✅ Grade check
        const gradeMatch =
          !filterGrade?.length ||
          filterGrade.some((g: any) => g.value === cc.grade);

        // ✅ Time period check
        const timeMatch =
          !timePeriodFilters.length ||
          timePeriodFilters.some(
            (tp: any) =>
              tp.value === cc.time_period && tp.grade === cc.grade
          );

        // ✅ Benchmark check
        let benchmarkMatch = true;

        // filterAdditionalBenchmark: 3 means false (hide at), 4 means true (show at)
        if (filterAdditionalBenchmark === 3 || filterAdditionalBenchmark === false) {
          benchmarkMatch = cc.order !== AT_ORDER_KEY;
        }
        if (filterAdditionalBenchmark === 4 || filterAdditionalBenchmark === true) {
          benchmarkMatch = true;
        }

        return {
          ...cc,
          isVisible: gradeMatch && timeMatch && benchmarkMatch
        };
      });

    setMeasurement((prev) =>
      prev
        ? {
            ...prev,
            color_criteria: filteredColorCriteria
          }
        : prev
    );
  };

  const handleResetColorCriteria = () => {
    if (props.measurement) {
      dispatch(showConfirmDialog({
        title: "Remove District Benchmarks",
        text: (
          <>
            <p className="text-center">Are you sure you want to remove the district benchmarks?</p>
            
            <p className="text-center">
              <FontAwesomeIcon 
                icon={faExclamationTriangle}
                style={{ color: 'red' }}
              /> {" "}
              <i>This action cannot be undone.</i>
            </p>
          </>
        ),
        confirmButtonText: "Yes, Remove",
        onConfirm: () => {
          props.onRemoveDefaultBenchmarks?.(true);
          props.onApplyChanges({
            ...props.measurement,
            color_criteria: [],
            display_name: props.measurement?.display_name ?? "",
          }, localSearchFilterState)
        }
      }));
    }
  }
  return (
    <Modal
      size="lg"
      show={props.showModal}
      centered
      style={{ backgroundColor: "rgb(255,255,255,0.8)" }}
      className="colorPickerModal"
      dialogClassName="colorPickerDialog"
      onHide={handleCloseModal}
      onShow={handleShowModal}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header
        closeButton
        className={"purpleModalHeader"}
      >
        <Modal.Title>{measurement?.color_criteria?.some((c) =>
          c.range_min != undefined
          && c.range_max != undefined
        ) && props.modes == 'view' ? "View Color Criteria For ": 
              (!measurement?.isAddNew && (props.benchmarkLabel ? props.benchmarkLabel.includes('Edit') : true) 
                ? "Edit Color Criteria For " 
                : (props.benchmarkLabel ? props.benchmarkLabel.includes('Set') : false) ? "Set Color Criteria For " : "Add Color Criteria For ")}
          {measurement?.display_name}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-inherit">
        {!measurement?.color_criteria ? (
          <LoadingIndicator />
        ) : (
          <>
            {isAllowedToEdit && (
              <>
              {props.modes !== 'view' ? (
                <>
                  {props.useGradeSpecificColor && <h4 className="text-left font-weight-bold">Select grade, benchmark level, and time period to continue.</h4>}
                </>
              ) : (
                  <div className="text-center">
                    <h3 className="mb-3 d-flex justify-content-center align-items-start" style={{gap: "0.5rem"}}>
                      <FontAwesomeIcon 
                        icon={faLock}
                      /> This is read-only mode.
                    </h3>
                  </div>
                )
              }
                <Form id="colorCriteriaForm" onSubmit={handleFormSubmit}>
                  {/* (props.enterDataManuallyType == 'academic') && */}
                  {props.useGradeSpecificColor &&
                    <div className="py-2 my-2 color-picker-container-rows">
                      <div className="row my-2">
                        <div className="col-6">
                          <h4 className="text-left mb-2 font-weight-bold">Grade
                            {props.modes !== 'view' 
                            ? <span className="label-asterisk">*</span> 
                            : null
                            }
                          </h4>
                          <Select
                            placeholder="Select"
                            options={options}
                            isMulti={true}
                            onChange={(event) => handleFilterGradeChange(event)}
                            value={localSearchFilterState?.filterGrade || []}
                            id="filterGradeSelect"
                            isDisabled={props.modes === 'view'}
                          />
                          <div className="mb-1 text-left" id="filterGradeSelectError" style={{ display: 'none'}}>
                            <small style={{ color: "red" }}>
                              <i>This field is required.</i>
                            </small>
                          </div>
                        </div>

                        <div className="col-6">
                          <h4 className="text-left mb-2 font-weight-bold">Benchmark Levels</h4>
                          <Select
                            placeholder="Select Benchmark Level"
                            options={[
                              { value: "false", label: "3 Benchmark Levels" },
                              { value: "true", label: "4 Benchmark Levels" },
                            ]}
                            isMulti={false}
                            value={{
                              value: String(localSearchFilterState?.filterAdditionalBenchmark),
                              label: localSearchFilterState?.filterAdditionalBenchmark
                                ? "4 Benchmark Levels"
                                : "3 Benchmark Levels",
                            }}
                            onChange={(option: any) =>
                              handleAdditionalBanchmarkChange({
                                target: { value: option.value },
                              } as unknown as React.FormEvent)
                            }
                            isDisabled={props.modes === 'view'}
                          />
                        </div>
                      </div>
                      {localSearchFilterState?.filterGrade.length > 0 &&
                        <>
                          <hr />
                          <div className="mb-2">
                            <h4 className="text-left mb-2 font-weight-bold">Time Period by Grade
                              {props.modes !== 'view' ? <span className="label-asterisk">*</span> : null}</h4>
                          </div>
                          <div className="row">
                            {[...(localSearchFilterState?.filterGrade || [])]
                              .sort((a: any, b: any) => {
                                const gradeOrder = ["K", ...Array.from({ length: 12 }, (_, i) => `G${i + 1}`)];
                                return gradeOrder.indexOf(a.value) - gradeOrder.indexOf(b.value);
                              })
                              .map((gradeObj: any, idx: number) => (
                                <div
                                  className="col-6"
                                  key={gradeObj.value}
                                  style={{
                                    marginTop: "10px"
                                  }}
                                >
                                  <div className="d-flex align-items-center text-left">
                                    <h4 className="mb-0" style={{ minWidth: '80px' }}>Grade {gradeObj.value}</h4>
                                    <div className="ml-3" style={{ flex: 1 }}>
                                      <Select
                                        isMulti={true}
                                        isDisabled={props.modes === 'view'}
                                        placeholder="Select Time Period"
                                        options={TimePeriodShortOptions.map((tp: ReactSelectOption<TimePeriod>) => ({
                                          ...tp,
                                          grade: gradeObj.value
                                        }))}
                                        onChange={(event) => handleFilterTimePeriodChange(event, gradeObj.value)}
                                        value={localSearchFilterState?.[`selectFor${gradeObj.value}`] || []}
                                      />
                                    </div>
                                  </div>
                                </div>
                              ))
                            }
                          </div>
                        </>
                      }
                    </div>

                  }
                  {props.modes !== 'view' && (
                    <>
                      <h4 className={`text-left font-weight-bold ${props.useGradeSpecificColor ? 'mt-4' : ''}`}>Add a level, define score ranges, and assign color criteria based on your selections.</h4>
                      <div className="my-3 text-left">
                        {props.useGradeSpecificColor && props.withReadingAlgorithm &&
                          <span className="alert alert-warning py-1 px-1" role="alert">
                            Note: Once you click on Apply, the Areas of Need will be updated based on district benchmarks you have entered.
                          </span>
                        }
                      </div>
                  </>
                  )}
                      <div className="color-picker-container">
                        <Table className="colorPickerTable" >
                      <thead>
                        <tr>
                          {props.withGrades && !isCategoryType && (
                            <>
                              <th  style={{width: '10%'}}>Grade</th>
                              <th  style={{width: '15%'}}>Time Period</th>
                            </>
                          )}
                          <th className="color-criteria-benchmark" style={{width: '30%'}}>Benchmark</th>
                          {IS_READY_COACH ? <th  className="color-criteria-level" style={{width: '20%'}}>Level{props.modes !== 'view' ? <span className="label-asterisk">*</span> : null}</th> : <th>Benchmark</th>}
                          {!isCategoryType && <th className="score-range" style={{width: '20%'}}>Score Range{props.modes !== 'view' ? <span className="label-asterisk">*</span> : null}</th>}
                          <th  className="color-criteria" style={{width: '20%'}}>Color Criteria{props.modes !== 'view' ? <span className="label-asterisk">*</span> : null}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {measurement?.color_criteria &&
                          measurement?.color_criteria.filter((colorCriteria) => props.useGradeSpecificColor ? colorCriteria.isVisible : true).map(
                            (colorCriteria: ColorCriteria, index: number) => (
                              // With Reading Algorithm, only show color criteria that match selected grade and time period. Otherwise, show all color criteria.
                              props.useGradeSpecificColor 
                              ? (
                                localSearchFilterState?.filterGrade?.length > 0 
                                && localSearchFilterState
                                    ?.filterGrade
                                    ?.filter((g: any) => 
                                      localSearchFilterState
                                    ?.[`selectFor${g.value}`].length > 0)
                                    ?.length > 0
                                ? (
                                    <React.Fragment key={index}>
                                      {(localSearchFilterState != undefined) && localSearchFilterState?.filterAdditionalBenchmark ?

                                        <tr id={`colorCriteriaRow_${index}`}>
                                          {!isCategoryType ? (
                                            <>
                                              {props.withGrades && (
                                                <>
                                                  <td>
                                                    <Form.Control
                                                      className="removeArrow"
                                                      disabled={props.modes === 'view' || true}
                                                      name={"grade"}
                                                      as="select"
                                                      value={props.filterDisabled ? "" : colorCriteria.grade}
                                                      onChange={handleColorCriteriaChange(
                                                        colorCriteria
                                                      )}
                                                    >
                                                      <option value={""}>All</option>
                                                      {GradeLevels.filter((grade) => currentUser.allowed_grades.length ? currentUser.allowed_grades.includes(grade.value) : true).map((gl) => (
                                                        <option key={gl.value} value={gl.value}>
                                                          {gl.value}
                                                        </option>
                                                      ))}
                                                    </Form.Control>
                                                  </td>

                                                  <td>
                                                    <Form.Control
                                                      className="removeArrow"
                                                      disabled={props.modes == 'view' || true}
                                                      name={"time_period"}
                                                      as="select"
                                                      value={props.filterDisabled ? "" : colorCriteria.time_period}
                                                      onChange={handleColorCriteriaChange(
                                                        colorCriteria
                                                      )}
                                                    >
                                                      <option value={""}>All</option>
                                                      {TimePeriodShortOptions.map((tp) => (
                                                        <option key={tp.value} value={tp.value}>
                                                          {tp.label}
                                                        </option>
                                                      ))}
                                                    </Form.Control>
                                                  </td>
                                                </>
                                              )}
                                              <td>
                                                <Form.Control
                                                  //className="removeArrow"
                                                  disabled={props.modes == 'view'}
                                                  name={"benchmark_status"}
                                                  as="select"
                                                  value={normalizeBenchmarkStatusValue(colorCriteria.benchmark_status)}
                                                  onChange={handleColorCriteriaChange(
                                                    colorCriteria
                                                  )}
                                                >
                                                  <option value="below">
                                                    Not Meeting Benchmarks
                                                  </option>
                                                  <option value="at">
                                                    Meeting Benchmarks
                                                  </option>
                                                  <option value="at_or_above">
                                                    Exceeding Benchmarks
                                                  </option>
                                                </Form.Control>
                                              </td>
                                              <td className="colorCriteriaDesc">
                                                <Form.Control
                                                  disabled={props.modes === 'view'}
                                                  required
                                                  name={"display_name"}
                                                  type="text"
                                                  className="stdInput"
                                                  onChange={handleColorCriteriaChange(
                                                    colorCriteria
                                                  )}
                                                  value={colorCriteria.display_name || ""}
                                                />
                                              </td>
                                              <td>
                                                <Form.Control
                                                  required
                                                  placeholder="Min"
                                                  name={"range_min"}
                                                  type="number"
                                                  className="inputSm"
                                                  min={measurement?.range_min}
                                                  pattern="[0-9]+"
                                                  ref={refCallback(colorCriteria)}
                                                  value={
                                                    colorCriteria.range_min === 0
                                                      ? "0"
                                                      : ((colorCriteria.range_min as unknown) as string) ||
                                                      ""
                                                  }
                                                  onChange={handleColorCriteriaChange(
                                                    colorCriteria
                                                  )}
                                                  disabled={props.modes === 'view'}
                                                />
                                                <strong>&nbsp;-&nbsp;</strong>
                                                <Form.Control
                                                  required
                                                  placeholder="Max"
                                                  name={"range_max"}
                                                  type="number"
                                                  className="inputSm"
                                                  pattern="[0-9]+"
                                                  max={measurement?.range_max}
                                                  value={
                                                    colorCriteria.range_max === 0
                                                      ? "0"
                                                      : ((colorCriteria.range_max as unknown) as string) ||
                                                      ""
                                                  }
                                                  onChange={handleColorCriteriaChange(
                                                    colorCriteria
                                                  )}
                                                  disabled={props.modes === 'view'}
                                                />
                                                <div id={`range_compare_${index}`} style={{ color: "red" }}></div>
                                                <div id={`range_overlap_${index}`} style={{ color: "red" }}></div>
                                              </td>
                                            </>
                                          ) : (
                                            <Form.Control
                                              required
                                              name={"selected_value_order"}
                                              as="select"
                                              value={
                                                (colorCriteria.selected_value_order as unknown) as string
                                              }
                                              onChange={handleColorCriteriaChange(
                                                colorCriteria
                                              )}
                                              disabled={props.modes == 'view' || props.useGradeSpecificColor}
                                            >
                                              <option key={-1} value={""} />
                                              {measurement?.possible_values
                                                ?.filter(
                                                  (possibleValue) =>
                                                    !measurement?.color_criteria!.find(
                                                      (cc) =>
                                                        cc.selected_value_order ===
                                                        possibleValue.order &&
                                                        cc.selected_value_order !==
                                                        colorCriteria.selected_value_order
                                                    )
                                                )
                                                .map((possibleValue) => (
                                                  <option
                                                    key={possibleValue.order}
                                                    value={possibleValue.order}
                                                  >
                                                    {possibleValue.display_name}
                                                  </option>
                                                ))}
                                            </Form.Control>
                                          )}
                                          <td>
                                            <div className={props.modes === 'view' ? 'disabled-picker' : ''}><GithubPicker
                                              color={colorCriteria.color}
                                              onChange={handleColorPickerChange(
                                                colorCriteria
                                              )}
                                              width="114px"
                                              triangle="hide"
                                              colors={['#ffd1da', '#ffefc1', '#bff9dd', '#deeaf6']}
                                            /></div>
                                          </td>
                                          <td>

                                            <div className={props.modes === 'view' ? 'disabled-sketch-picker' : ''}><SketchColorPicker
                                              readonly={props.useGradeSpecificColor}
                                              color={colorCriteria.color}
                                              handleColorPickerClose={handleColorPickerClose}
                                              handleColorPickerChange={handleColorPickerChange(
                                                colorCriteria
                                              )}
                                              handleColorPickerClick={handleColorPickerClick(
                                                index
                                              )}
                                              displayColorPicker={
                                                colorPicker.display &&
                                                colorPicker.target === index
                                              }
                                            /></div>
                                          </td>
                                          <td>
                                            {(
                                              (!props.useGradeSpecificColor && !props.withReadingAlgorithm)
                                              && (
                                                index <= 4 &&
                                                measurement?.color_criteria &&

                                                (measurement.color_criteria.length > 2 &&
                                                  measurement.color_criteria.length <= 4
                                                )
                                              )
                                            ) && (
                                                <Button
                                                  onClick={handleColorCriteriaRemove(
                                                    colorCriteria,
                                                    index
                                                  )}
                                                >
                                                  <FontAwesomeIcon
                                                    icon="times-circle"
                                                    style={{
                                                      color: "red",
                                                      cursor: "pointer",
                                                    }}
                                                  />
                                                </Button>
                                              )}
                                          </td>
                                        </tr>
                                        :
                                        (colorCriteria.order !== AT_ORDER_KEY) &&
                                        <tr id={`colorCriteriaRow_${index}`}>
                                          {!isCategoryType ? (
                                            <>
                                              {props.withGrades && (
                                                <>
                                                  <td>
                                                    <Form.Control
                                                      className="removeArrow"
                                                      disabled={props.modes === 'view' || true}
                                                      name={"grade"}
                                                      as="select"
                                                      value={colorCriteria.grade}
                                                      onChange={handleColorCriteriaChange(
                                                        colorCriteria
                                                      )}
                                                    >
                                                      <option value={""}>All</option>
                                                      {GradeLevels.filter((grade) => currentUser.allowed_grades.length ? currentUser.allowed_grades.includes(grade.value) : true).map((gl) => (
                                                        <option key={gl.value} value={gl.value}>
                                                          {gl.value}
                                                        </option>
                                                      ))}
                                                    </Form.Control>
                                                  </td>

                                                  <td>
                                                    <Form.Control
                                                      className="removeArrow"
                                                      disabled={props.modes === 'view' || true}
                                                      name={"time_period"}
                                                      as="select"
                                                      value={colorCriteria.time_period}
                                                      onChange={handleColorCriteriaChange(
                                                        colorCriteria
                                                      )}
                                                    >
                                                      <option value={""}>All</option>
                                                      {TimePeriodShortOptions.map((tp) => (
                                                        <option key={tp.value} value={tp.value}>
                                                          {tp.label}
                                                        </option>
                                                      ))}
                                                    </Form.Control>
                                                  </td>
                                                </>
                                              )}
                                              <td>
                                                <Form.Control
                                                  // className="removeArrow"
                                                  disabled={props.modes === 'view'}
                                                  name={"benchmark_status"}
                                                  as="select"
                                                  value={normalizeBenchmarkStatusValue(colorCriteria.benchmark_status)}
                                                  onChange={handleColorCriteriaChange(
                                                    colorCriteria
                                                  )}
                                                >
                                                  <option value="below">
                                                    Not Meeting Benchmarks
                                                  </option>
                                                  <option value="at">
                                                    Meeting Benchmarks
                                                  </option>
                                                  <option value="at_or_above">
                                                    Exceeding Benchmarks
                                                  </option>
                                                </Form.Control>
                                              </td>
                                              <td className="colorCriteriaDesc">
                                                <Form.Control
                                                  disabled={props.modes === 'view'}
                                                  required
                                                  name={"display_name"}
                                                  type="text"
                                                  className="stdInput"
                                                  onChange={handleColorCriteriaChange(
                                                    colorCriteria
                                                  )}
                                                  value={colorCriteria.display_name || ""}
                                                />
                                              </td>
                                              <td>
                                                <Form.Control
                                                  required
                                                  placeholder="Min"
                                                  name={"range_min"}
                                                  type="number"
                                                  className="inputSm"
                                                  min={measurement?.range_min}
                                                  pattern="[0-9]+"
                                                  ref={refCallback(colorCriteria)}
                                                  value={
                                                    colorCriteria.range_min === 0
                                                      ? "0"
                                                      : ((colorCriteria.range_min as unknown) as string) ||
                                                      ""
                                                  }
                                                  onChange={handleColorCriteriaChange(
                                                    colorCriteria
                                                  )}
                                                  disabled={props.modes === 'view'}
                                                />
                                                <strong>&nbsp;-&nbsp;</strong>
                                                <Form.Control
                                                  required
                                                  placeholder="Max"
                                                  name={"range_max"}
                                                  type="number"
                                                  className="inputSm"
                                                  pattern="[0-9]+"
                                                  max={measurement?.range_max}
                                                  value={
                                                    colorCriteria.range_max === 0
                                                      ? "0"
                                                      : ((colorCriteria.range_max as unknown) as string) ||
                                                      ""
                                                  }
                                                  onChange={handleColorCriteriaChange(
                                                    colorCriteria
                                                  )}
                                                  disabled={props.modes === 'view'}
                                                />
                                                <div id={`range_compare_${index}`} style={{ color: "red" }}></div>
                                                <div id={`range_overlap_${index}`} style={{ color: "red" }}></div>
                                              </td>
                                            </>
                                          ) : (
                                            <Form.Control
                                              required
                                              name={"selected_value_order"}
                                              as="select"
                                              value={
                                                (colorCriteria.selected_value_order as unknown) as string
                                              }
                                              onChange={handleColorCriteriaChange(
                                                colorCriteria
                                              )}
                                              disabled={props.modes === 'view' || props.useGradeSpecificColor}
                                            >
                                              <option key={-1} value={""} />
                                              {measurement?.possible_values
                                                ?.filter(
                                                  (possibleValue) =>
                                                    !measurement?.color_criteria!.find(
                                                      (cc) =>
                                                        cc.selected_value_order ===
                                                        possibleValue.order &&
                                                        cc.selected_value_order !==
                                                        colorCriteria.selected_value_order
                                                    )
                                                )
                                                .map((possibleValue) => (
                                                  <option
                                                    key={possibleValue.order}
                                                    value={possibleValue.order}
                                                  >
                                                    {possibleValue.display_name}
                                                  </option>
                                                ))}
                                            </Form.Control>
                                          )}
                                          <td>
                                            <div className={props.modes === 'view' ? 'disabled-picker' : ''}><GithubPicker
                                              color={colorCriteria.color}
                                              onChange={handleColorPickerChange(colorCriteria)}
                                              width="114px"
                                              triangle="hide"
                                              colors={[
                                                "#ffd1da",
                                                "#ffefc1",
                                                "#bff9dd",
                                                "#deeaf6",
                                              ]}
                                              
                                            /></div>
                                          </td>
                                          <td>
                                            <div className={props.modes === 'view' ? 'disabled-sketch-picker' : ''}><SketchColorPicker
                                              readonly={props.useGradeSpecificColor}
                                              color={colorCriteria.color}
                                              handleColorPickerClose={handleColorPickerClose}
                                              handleColorPickerChange={handleColorPickerChange(
                                                colorCriteria
                                              )}
                                              handleColorPickerClick={handleColorPickerClick(
                                                index
                                              )}
                                              displayColorPicker={
                                                colorPicker.display &&
                                                colorPicker.target === index
                                              }
                                            /></div>
                                          </td>
                                          <td>
                                            {
                                              // Show remove button for index 0 to 4, but only if there are at least 3 color_criteria left after removal for grade-specific/reading algo,
                                              // or at least 2 for non-grade-specific/non-reading-algo.
                                              (
                                                (!props.useGradeSpecificColor && !props.withReadingAlgorithm)
                                                && (
                                                  index <= 4 &&
                                                  measurement?.color_criteria &&

                                                  (measurement.color_criteria.length > 2 &&
                                                    measurement.color_criteria.length <= 4
                                                  )
                                                )
                                              ) && (
                                                <Button
                                                  onClick={handleColorCriteriaRemove(
                                                    colorCriteria,
                                                    index
                                                  )}
                                                >
                                                  <FontAwesomeIcon
                                                    icon="times-circle"
                                                    style={{
                                                      color: "red",
                                                      cursor: "pointer",
                                                    }}
                                                  />
                                                </Button>
                                              )}
                                          </td>
                                        </tr>
                                      }
                                    </React.Fragment>
                                  ) : (index == 0) && <tr>
                                    <td colSpan={6}>
                                      <div style={{marginTop: "20px"}}>
                                        <p>
                                          Please select a <strong>Grade</strong> and corresponding <strong>Time Period</strong> to proceed.
                                        </p>
                                      </div>
                                    </td>
                                  </tr>
                                ) : (
                                  // Without Reading Algorithm, show all color criteria that are not at benchmark status.
                                  <tr id={`colorCriteriaRow_${index}`}>
                                    {!isCategoryType ? (
                                      <>
                                        {props.withGrades && (
                                          <>
                                          <td>
                                            <Form.Control
                                              className="removeArrow"
                                              disabled={props.modes === 'view' || true}
                                              name={"grade"}
                                              as="select"
                                              value={colorCriteria.grade}
                                              onChange={handleColorCriteriaChange(
                                                colorCriteria
                                              )}
                                            >
                                              <option value={""}>All</option>
                                              {GradeLevels.filter((grade) => currentUser.allowed_grades.length ? currentUser.allowed_grades.includes(grade.value) : true).map((gl) => (
                                                <option key={gl.value} value={gl.value}>
                                                  {gl.value}
                                                </option>
                                              ))}
                                            </Form.Control>
                                          </td>

                                          <td>
                                            <Form.Control
                                              className="removeArrow"
                                              disabled={props.modes === 'view' || true}
                                              name={"time_period"}
                                              as="select"
                                              value={colorCriteria.time_period}
                                              onChange={handleColorCriteriaChange(
                                                colorCriteria
                                              )}
                                            >
                                              <option value={""}>All</option>
                                              {TimePeriodShortOptions.map((tp) => (
                                                <option key={tp.value} value={tp.value}>
                                                  {tp.label}
                                                </option>
                                              ))}
                                            </Form.Control>
                                          </td>
                                        </>
                                      )}
                                      <td>
                                        <Form.Control
                                          // className="removeArrow"
                                          disabled={props.modes === 'view'}
                                          name={"benchmark_status"}
                                          as="select"
                                          value={normalizeBenchmarkStatusValue(colorCriteria.benchmark_status)}
                                          onChange={handleColorCriteriaChange(
                                            colorCriteria
                                          )}
                                        >
                                          <option value="below">
                                            Not Meeting Benchmarks
                                          </option>
                                          <option value="at">
                                            Meeting Benchmarks
                                          </option>
                                          <option value="at_or_above">
                                            Exceeding Benchmarks
                                          </option>
                                        </Form.Control>
                                      </td>
                                      <td className="colorCriteriaDesc">
                                        <Form.Control
                                          disabled={props.modes === 'view'}
                                          required
                                          name={"display_name"}
                                          type="text"
                                          className="stdInput"
                                          onChange={handleColorCriteriaChange(
                                            colorCriteria
                                          )}
                                          value={colorCriteria.display_name || ""}
                                        />
                                      </td>
                                      <td>
                                        <Form.Control
                                          required
                                          placeholder="Min"
                                          name={"range_min"}
                                          type="number"
                                          className="inputSm"
                                          min={measurement?.range_min}
                                          pattern="[0-9]+"
                                          ref={refCallback(colorCriteria)}
                                          value={
                                            colorCriteria.range_min === 0
                                              ? "0"
                                              : ((colorCriteria.range_min as unknown) as string) ||
                                              ""
                                          }
                                          onChange={handleColorCriteriaChange(
                                            colorCriteria
                                          )}
                                          disabled={props.modes === 'view'}
                                        />
                                        <strong>&nbsp;-&nbsp;</strong>
                                        <Form.Control
                                          required
                                          placeholder="Max"
                                          name={"range_max"}
                                          type="number"
                                          className="inputSm"
                                          pattern="[0-9]+"
                                          max={measurement?.range_max}
                                          value={
                                            colorCriteria.range_max === 0
                                              ? "0"
                                              : ((colorCriteria.range_max as unknown) as string) ||
                                              ""
                                          }
                                          onChange={handleColorCriteriaChange(
                                            colorCriteria
                                          )}
                                          disabled={props.modes === 'view'}
                                        />
                                        <div id={`range_compare_${index}`} style={{ color: "red" }}></div>
                                        <div id={`range_overlap_${index}`} style={{ color: "red" }}></div>
                                      </td>
                                    </>
                                  ) : (
                                    <Form.Control
                                      required
                                      name={"selected_value_order"}
                                      as="select"
                                      value={
                                        (colorCriteria.selected_value_order as unknown) as string
                                      }
                                      onChange={handleColorCriteriaChange(
                                        colorCriteria
                                      )}
                                      disabled={props.modes === 'view' || props.useGradeSpecificColor}
                                    >
                                      <option key={-1} value={""} />
                                      {measurement?.possible_values
                                        ?.filter(
                                          (possibleValue) =>
                                            !measurement?.color_criteria!.find(
                                              (cc) =>
                                                cc.selected_value_order ===
                                                possibleValue.order &&
                                                cc.selected_value_order !==
                                                colorCriteria.selected_value_order
                                            )
                                        )
                                        .map((possibleValue) => (
                                          <option
                                            key={possibleValue.order}
                                            value={possibleValue.order}
                                          >
                                            {possibleValue.display_name}
                                          </option>
                                        ))}
                                    </Form.Control>
                                  )}
                                  <td>
                                    <div className={props.modes === 'view' ? 'disabled-picker' : ''}><GithubPicker
                                      color={colorCriteria.color}
                                      onChange={handleColorPickerChange(colorCriteria)}
                                      width="114px"
                                      triangle="hide"
                                      colors={[
                                        "#ffd1da",
                                        "#ffefc1",
                                        "#bff9dd",
                                        "#deeaf6",
                                      ]}
                                    /></div>
                                  </td>
                                  <td>
                                    <div className={props.modes === 'view' ? 'disabled-sketch-picker' : ''}><SketchColorPicker
                                      readonly={props.useGradeSpecificColor}
                                      color={colorCriteria.color}
                                      handleColorPickerClose={handleColorPickerClose}
                                      handleColorPickerChange={handleColorPickerChange(
                                        colorCriteria
                                      )}
                                      handleColorPickerClick={handleColorPickerClick(
                                        index
                                      )}
                                      displayColorPicker={
                                        colorPicker.display &&
                                        colorPicker.target === index
                                      }
                                    /></div>
                                  </td>
                                  <td>
                                    {
                                      // Show remove button for index 0 to 4, but only if there are at least 3 color_criteria left after removal for grade-specific/reading algo,
                                      // or at least 2 for non-grade-specific/non-reading-algo.
                                      (
                                        (!props.useGradeSpecificColor && !props.withReadingAlgorithm)
                                        && (
                                          index <= 4 &&
                                          measurement?.color_criteria &&

                                          (measurement.color_criteria.length > 2 &&
                                            measurement.color_criteria.length <= 4
                                          )
                                        ) && (props.modes !== 'view')
                                      ) && (
                                        <Button
                                          onClick={handleColorCriteriaRemove(
                                            colorCriteria,
                                            index
                                          )}
                                        >
                                          <FontAwesomeIcon
                                            icon="times-circle"
                                            style={{
                                              color: "red",
                                              cursor: "pointer",
                                            }}
                                          />
                                        </Button>
                                      )}
                                  </td>
                                </tr>
                              )

                            )
                          )}
                        {!props.useGradeSpecificColor &&
                          (!isCategoryType ||
                            (isCategoryType &&
                              measurement?.color_criteria?.length !==
                              measurement?.possible_values?.length)) &&
                          ((!props.useGradeSpecificColor && !props.withReadingAlgorithm)
                            && (
                              measurement?.color_criteria &&
                              measurement.color_criteria.length >= 2 &&
                              measurement.color_criteria.length < 4
                            )
                          ) && (props.modes !== 'view') && (
                            <tr>
                              <td
                                colSpan={
                                  isCategoryType ? 2 : props.withGrades ? 5 : 3
                                }
                              />
                              <td>
                                <Button onClick={handleColorCriteriaAdd}>
                                  {" "}
                                  <FontAwesomeIcon
                                    icon="plus-circle"
                                    style={{ color: "#2A74D1", cursor: "pointer" }}
                                  />
                                </Button>
                              </td>
                            </tr>
                          )}
                      </tbody>
                    </Table>
                  </div>
                </Form>
                {props.modes !== 'view' && (
                  <div className="btnActions row">
                    <div className="col-md-4 text-left">
                      <button type="button" className="whiteBtnSm" onClick={handleCloseModal}>
                        <FormattedMessage id="app.forms.buttons.cancel" />
                      </button>
                    </div>
                    <div className="col-md-4">
                      {props.measurement?.color_criteria?.some((colorCriteria) =>
                        colorCriteria.range_min != undefined && colorCriteria.range_max != undefined)
                        && props.isSystemAssessment && (
                          <button
                            type="button"
                            className="redBtnSm"
                            onClick={handleResetColorCriteria}
                            disabled={props.isRemoveDefaultBenchmarks && props.isLoading}
                          >
                            Remove District Benchmarks{" "}
                            {props.isRemoveDefaultBenchmarks && props.isLoading && <Spinner animation="border" size="sm" />}
                          </button>
                        )}
                    </div>
                    <div className="col-md-4 text-right pr-0">
                      <button
                        type="submit"
                        className="blueBtnSm colorPickerApplyButton"
                        form="colorCriteriaForm"
                        disabled={!props.isRemoveDefaultBenchmarks && props.isLoading}
                      >
                        Apply{" "}
                        {!props.isRemoveDefaultBenchmarks && props.isLoading && <Spinner animation="border" size="sm" />}
                      </button>
                    </div>
                  </div>
                )}
                {props.modes === 'view' && (
                   <div className="text-center">
                    <button type="button" className="blueBtnSm" onClick={handleCloseModal}>
                      Close
                    </button>
                  </div>
                )}
              </>
            )}

            {!isAllowedToEdit && (
              <>
                <h6>Insufficient Permission</h6>
                <h3 className="font-weight-bold my-3">
                  {
                    "Please contact your district administrator for permission to edit."
                  }
                </h3>
                <label className="blueBtnSm colorPickerApplyButton">
                  <Button
                    type="submit"
                    form="colorCriteriaForm"
                    onClick={handleCloseModal}
                  >
                    Close
                  </Button>
                </label>
              </>
            )}
          </>
        )}

      </Modal.Body>
    </Modal>
  );
};

const mapStateToProps = ({ onboarding, groups }: ApplicationState): PropsFromState => {
  return {
    enterDataManuallyType: onboarding.modalsState.enterDataManuallyType,
    //searchFilter: groups.searchFilter
  };
};


export default connect(
  mapStateToProps,
  null
)(EditMeasureColorCriteriaModal);